import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useMediaQuery, PHONE_QUERY } from '../../lib/useMediaQuery';
import { useSlide } from '../../lib/useSlide';
import { Icon } from './Icon';
import { useUnsavedGuard } from './unsavedGuard';

export interface MasterDetailShellProps {
  title: string;
  modeLabel: string;
  /**
   * THE BAR'S SUBJECT, WHERE A PLAIN NAME IS NOT ENOUGH (Freddi, 2026-09-19).
   *
   * The shell drew `‹ Ann Ryan · booking ✕` and the booking pane drew its own
   * header underneath carrying the same household again — two bars, the name
   * twice, and the identity sitting in the lower one where it read as the
   * form's first field rather than as the modal's subject.
   *
   * Given, this replaces `title · modeLabel` entirely. The caller owns what the
   * bar says, because only the caller knows which pane is open. Omitted,
   * nothing changes for the four other modals that use this shell.
   */
  head?: ReactNode;
  onClose: () => void;
  // The left rail (RailSearch + RailTimeline/RailRecents). Given a function, it
  // is handed `closeRail` — at the phone tier the rail is a drawer OVER the
  // pane, so choosing from it has to dismiss it; left open, the choice is made
  // and then hidden behind the thing the user just chose.
  //
  // OMITTED ENTIRELY for a record with nothing beside it. A viewing has no
  // history, no siblings and no list to pick from, and an empty rail is a blank
  // fixed-width column plus a phone-tier toggle that opens nothing. When absent,
  // the rail, its scrim and its toggle are not rendered and the pane takes the
  // whole card. One shell, one more case — rather than a second shell that would
  // have to keep its Escape handling, its sheet lifecycle and its exit contract
  // in step with this one.
  rail?: ReactNode | ((api: { closeRail: () => void }) => ReactNode);
  // The contextual pane. A FUNCTION receives the shell's own dismissal — the
  // one that plays the drawer out before it calls back — for a pane that is not
  // a separate component and so cannot reach `useShellDismiss` through the
  // context. `ViewingModal` is that case: it builds its own header from its own
  // state, so its body cannot be lifted into a child without the title losing
  // sight of the name it names (codex, PR #172).
  children: ReactNode | ((dismiss: () => void) => ReactNode);
  className?: string; // extra modifier on .obx (e.g. 'staff-obx')
  // Names the rail on the phone-tier toggle — each modal's rail holds something
  // different (a booking history, a staff list). Defaults to a neutral "List".
  railLabel?: string;
  // WHAT BACK DOES. The control is ALWAYS rendered (Freddi, replacing AC4) —
  // absent, it comes and goes and the header jumps; present but inert, it says
  // plainly that this record has nothing behind it. So `undefined` here means
  // disabled, not missing.
  onBack?: () => void;
  // Whether THIS back leaves the modal. Search's does — the record returns to
  // the results, so the sheet retraces its push. A step taken INSIDE the modal
  // (a stay picked from the rail, the owner editor) does not: the pane changes
  // and the sheet stays where it is.
  backExits?: boolean;
  // Whether the modal ARRIVED by forward navigation, which is a different
  // question from where back currently goes: reached from a search result it
  // pushes in from the right for its whole life, however deep the stack gets.
  // Defaults to `backExits` for callers with only the one kind of back.
  pushed?: boolean;
  /** Names the arrow for a screen reader; there is no visible label. */
  backLabel?: string;
  // A THIRD COLUMN, to the right of the pane. The household modal's App access
  // column is the first of them (household-app-access, R-DESK-16): a fixed rail
  // that belongs to the record rather than to the form, so it must not scroll
  // with the pane or be rebuilt each time the pane changes.
  //
  // Optional, and absent everywhere else — the booking, staff and account
  // modals have nothing to put there, and an empty fixed-width column is worse
  // than none. Below the tablet tier the stylesheet wraps it under the pane
  // rather than squeezing both.
  aside?: ReactNode;
}

// THE SHELL OWNS THE EXIT, so anything that closes the modal has to ask it.
// The panes were each handed the raw `onClose` prop instead, which unmounts the
// modal where it stands: the X played the sheet down and Cancel tore it out of
// the air. Every way out is now the same way out.
const ShellExit = createContext<{ dismiss: () => void } | null>(null);

// The pane's own way of closing the modal — Cancel, a completed save, a delete.
// Outside a shell (a pane rendered somewhere else, and in unit tests) there is
// no exit to play, so the fallback is called straight through.
export function useShellDismiss(fallback: () => void): () => void {
  const api = useContext(ShellExit);
  const dismiss = api?.dismiss;
  return useCallback(() => (dismiss ?? fallback)(), [dismiss, fallback]);
}

/**
 * The dismissal a pane uses AFTER it has committed — close-first on Save, the
 * close after Delete or Archive. The same exit as `useShellDismiss`, with the
 * unsaved-changes guard released first: what was just written is not an
 * unsaved edit, and the guard cannot tell a save's close from a Cancel.
 */
export function useSavedDismiss(fallback: () => void): () => void {
  const dismiss = useShellDismiss(fallback);
  const { release } = useUnsavedGuard();
  return useCallback(() => {
    release();
    dismiss();
  }, [release, dismiss]);
}

// The master-detail modal shell shared by EVERY modal — owner↔booking, staff and
// account: the .obx centered card with a header, the fixed-width rail, and the
// contextual pane. Owns Escape-to-close (never while a field is focused).
//
// At the phone tier it is TWO drawers, and they are deliberately ONE mechanism:
// the sheet and the rail both run on `useSlide`, so their open/close lifecycles
// cannot drift apart. The stylesheet gives them the prototype's timings — 0.3s
// in on cubic-bezier(.2,.7,.2,1), 0.26s out on cubic-bezier(.4,0,.9,.4) — and
// the hook gives them the same two guarantees: the phase class lands in the same
// commit as the open flag, and it clears on `animationend`, not on a stopwatch.
export function MasterDetailShell({
  title,
  modeLabel,
  head,
  onClose,
  rail,
  children,
  className,
  railLabel = 'List',
  onBack,
  backExits,
  pushed: pushedProp,
  backLabel = 'Back',
  aside,
}: MasterDetailShellProps) {
  // THE UNSAVED-CHANGES GUARD, FOR EVERY MODAL ON THIS SHELL. One provider at
  // the app root (AppShell), one hook here: every way out — Escape, the close
  // button, a pane's Cancel through `useShellDismiss` — asks it first, and it
  // proceeds at once or puts Discard changes? on screen. A pane declares its
  // draft with `useDirtyDraft`; nothing else is wired per modal.
  const { guard: guardDismiss } = useUnsavedGuard();
  const railId = useId();
  const phone = useMediaQuery(PHONE_QUERY);

  // Drawer one — the rail, down from the top of the body.
  const railSlide = useSlide(false);
  const railOpen = railSlide.open;
  const railAnim = railSlide.phase === 'in' ? 'drop' : railSlide.phase === 'out' ? 'closing' : '';

  // Drawer two — the sheet itself, up from the bottom. It is mounted by its
  // parent, so "open" starts true; closing means playing the exit and only THEN
  // handing control back, or React unmounts it mid-slide.
  const sheet = useSlide(true);
  const pendingDone = useRef<(() => void) | null>(null);
  // Arriving FROM somewhere (a search result) is forward navigation, so the
  // sheet pushes in from the right and leaves back to the right — the same
  // gesture SlidePane gives the pane. Opened straight off a page it rises from
  // the bottom, because nothing was pushed aside to get here.
  const pushed = phone && (pushedProp ?? !!backExits);
  const [leaveRight, setLeaveRight] = useState(false);
  const departNow = useCallback(
    (done: () => void, viaBack = false) => {
      if (!phone) {
        done();
        return;
      }
      if (!sheet.open) return; // already on its way out
      setLeaveRight(viaBack && !!onBack);
      pendingDone.current = done;
      sheet.setOpen(false);
    },
    [phone, sheet, onBack],
  );
  // ONE CHOKE POINT for leaving. The guard sits in front of the departure, not
  // inside the callback it plays: a drawer that had already slid out by the
  // time "Keep editing" was pressed would have nothing to keep.
  const depart = useCallback(
    (done: () => void, viaBack = false) => guardDismiss(() => departNow(done, viaBack)),
    [guardDismiss, departNow],
  );
  // The exit is over when the hook says so: closed AND no longer moving.
  useEffect(() => {
    if (sheet.open || sheet.phase !== '' || !pendingDone.current) return;
    const done = pendingDone.current;
    pendingDone.current = null;
    done();
  }, [sheet.open, sheet.phase]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // THE RAIL COMES FIRST, ahead of the field guard. The rail's own search
      // input is inside the drawer, so the guard below made the rail-close
      // branch unreachable from the one place a user is most likely to press
      // Escape — RailSearch only clears its query and never closes anything, so
      // the drawer stayed open however many times it was pressed.
      if (hasRail && railOpen) {
        railSlide.setOpen(false);
        return;
      }
      const ae = document.activeElement;
      if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA')) return;
      // Then back to whatever opened this, then out of the modal entirely.
      if (onBack) {
        // A step back INSIDE the modal is the host's own navigation and the
        // host guards it (OwnerBookingModal wraps goBack); only an exiting
        // Back comes through `depart`.
        if (backExits) depart(onBack, true);
        else onBack();
        return;
      }
      depart(onClose);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose, onBack, backExits, railOpen, railSlide, depart]);

  // Cancel is a DISMISSAL, not a step back — so it takes the same route as the
  // X (sink), never the back control's exit to the right, even when this modal
  // was pushed here from a search result.
  const exit = useMemo(() => ({ dismiss: () => depart(onClose) }), [depart, onClose]);
  // `rail` may legitimately be null or undefined — a record with nothing beside
  // it. `false` and '' are not expected but would read as "no rail" too, which
  // is the safe way round.
  const hasRail = rail !== undefined && rail !== null;

  return (
    <ShellExit.Provider value={exit}>
    <div
      className={
        'obx' +
        (className ? ' ' + className : '') +
        // A THIRD COLUMN MAKES THE CARD WIDER, it does not divide it up. The
        // card is 960px, which is the rail and the pane; dropping a 340px
        // column into that squeezed both and shrank every field in the
        // household form (Freddi, 2026-09-12). The prototype draws this modal
        // at 1310px, which is exactly 960 plus the column.
        (aside ? ' has-aside' : '') +
        (sheet.open ? '' : ' is-leaving') +
        (pushed ? ' is-pushed' : '') +
        (leaveRight ? ' is-leaving-right' : '')
      }
      data-rail={railOpen ? 'open' : 'closed'}
    >
      <div className="card" onAnimationEnd={sheet.onAnimationEnd}>
        {/* The sheet's grab handle — phone tier only, where the card is a sheet
            anchored to the bottom rather than a centred dialog. */}
        <div className="obx-grip" aria-hidden="true" />
        <div className="chead">
          <button
            type="button"
            className="obx-back"
            disabled={!onBack}
            aria-label={backLabel}
            title={onBack ? backLabel : 'Nothing to go back to'}
            onClick={() => {
              if (!onBack) return;
              if (backExits) depart(onBack, true);
              else onBack();
            }}
          >
            <Icon name="left" size={15} />
          </button>
          {hasRail && (
            <button
              type="button"
              className="rail-toggle"
              aria-expanded={railOpen}
              aria-controls={railId}
              onClick={() => railSlide.setOpen((o) => !o)}
            >
              <Icon name="list" size={15} />
              <span className="rt-txt">{railLabel}</span>
              <Icon name="chevron-down" size={13} className="rt-chev" />
            </button>
          )}
          {head ?? (
            <>
              <strong>{title}</strong>
              <span className="mut">· {modeLabel}</span>
            </>
          )}
          <button type="button" className="x" onClick={() => depart(onClose)} aria-label="Close"><Icon name="close" size={15} /></button>
        </div>
        <div className={'body' + (hasRail ? '' : ' no-rail')}>
          {hasRail && (
          <div
            className={'rail' + (railAnim ? ' ' + railAnim : '')}
            id={railId}
            onAnimationEnd={railSlide.onAnimationEnd}
          >
            {/* Deliberately NOT deferred. Holding the rail's contents back until
                first opened saved 12ms of the sheet's mount and cost the rail
                its own entrance: empty it has no height, so it sat at the body's
                top instead of parked above it, and flashed as a strip before
                sliding. A cheaper mount is not worth a broken drawer. */}
            {typeof rail === 'function'
              ? rail({ closeRail: () => railSlide.setOpen(false) })
              : rail}
          </div>
          )}
          {/* Dismisses the phone-tier rail by tapping the pane behind it.
              Inert (pointer-events: none, opacity 0) while the rail is closed
              and at every width above the phone tier. */}
          {hasRail && (
          <div
            className={'railscrim' + (railAnim === 'drop' ? ' fade' : railAnim === 'closing' ? ' unfade' : '')}
            onClick={() => railSlide.setOpen(false)}
          />
          )}
          <div className="panebox">{typeof children === 'function' ? children(exit.dismiss) : children}</div>
          {aside}
        </div>
      </div>
    </div>
    </ShellExit.Provider>
  );
}
